/* eslint-disable react/no-danger */
import React, { FunctionComponent, useState } from 'react';
import { PrimaryButton } from 'app/common/components/controls/button/button';
import { OnboardingDialog } from './OnboardingDialog';
import { useAlert, AlertContainer } from 'app/gamma/src/components/alert';
import { AtlassianAccountOnboardingLinks } from './AtlassianAccountOnboardingLinks';
// eslint-disable-next-line @trello/less-matches-component
import styles from './AtlassianAccountOnboardingOverlay.less';
import api from 'app/gamma/src/api';
import { forTemplate, forNamespace } from '@trello/app/common/lib/util/i18n';
import { ProfileSyncResponse } from 'app/gamma/src/types/responses';

const ATLASSIAN_PROFILE_SYNC_ALERT_AREA = 'ATLASSIAN_PROFILE_SYNC_ALERT_AREA';
const ATLASSIAN_PROFILE_SYNC_ALERT_NAME = 'ATLASSIAN_PROFILE_SYNC_ALERT_NAME';
const PATHNAME = window.location.pathname;

const format = forNamespace(['aa onboarding', 'dialog']);
const formatError = forTemplate('app_management');

interface LoginWithAtlassianProps {
  aaEmail: string;
  orgName: string;
  onDismiss: () => void;
  onClick: () => void;
  image?: HTMLImageElement;
  profileSyncResponseProvider?: () => Promise<ProfileSyncResponse>;
}

/**
 * Extracted to allow testing/storybooking this component.
 * Not expecting this property to be overridden by consumers.
 */
const defaultProfileSyncResponseProvider = () => {
  return api.client.rest.post<ProfileSyncResponse>(
    'members/me/atlassianAccount/setup',
    {
      query: {
        returnUrl: PATHNAME,
      },
    },
  );
};

export const LoginWithAtlassian: FunctionComponent<LoginWithAtlassianProps> = ({
  image,
  aaEmail,
  orgName,
  onDismiss,
  onClick,
  profileSyncResponseProvider = defaultProfileSyncResponseProvider,
}) => {
  const { showAlert: showErrorAlert } = useAlert({
    alertArea: ATLASSIAN_PROFILE_SYNC_ALERT_AREA,
    alertName: ATLASSIAN_PROFILE_SYNC_ALERT_NAME,
  });

  const [requiresEmailVerification, setRequiresEmailVerification] = useState(
    false,
  );

  const atlassianLogin = async () => {
    onClick();
    try {
      const response = await profileSyncResponseProvider();
      if (response.emailVerificationRequired) {
        setRequiresEmailVerification(true);
      } else {
        window.location.assign(response.redirect_url);
      }
    } catch (err) {
      if (err.error_code === 'ALREADY_MASTERED') {
        onDismiss();
        return;
      }
      showErrorAlert();
    }
  };

  if (requiresEmailVerification) {
    return (
      <OnboardingDialog
        title={format('check your inbox')}
        helpLink={AtlassianAccountOnboardingLinks.Help}
        closeDialog={onDismiss}
        image={image}
      >
        <div>
          <p>
            {format('we sent an email', {
              email: <strong key="email">{aaEmail}</strong>,
            })}
          </p>
          <p>{format('click the link in the email')}</p>
        </div>
      </OnboardingDialog>
    );
  }

  return (
    <>
      <AlertContainer
        displayType="error"
        name={ATLASSIAN_PROFILE_SYNC_ALERT_NAME}
        area={ATLASSIAN_PROFILE_SYNC_ALERT_AREA}
      >
        {formatError('something-went-wrong')}
      </AlertContainer>
      <OnboardingDialog
        title={format('sounds good')}
        helpLink={AtlassianAccountOnboardingLinks.Help}
        closeDialog={onDismiss}
        image={image}
      >
        <p
          dangerouslySetInnerHTML={{
            __html: format('managed account message', {
              transferBoardsUrl: AtlassianAccountOnboardingLinks.TransferBoards,
            }),
          }}
        />
        <div>
          <p>{format('primary email message')}</p>
        </div>
        <div className={styles.primaryEmail}>{aaEmail}</div>

        <p
          dangerouslySetInnerHTML={{
            __html: format('log in with Atlassian', {
              org: orgName,
              atlassianAccountUrl:
                AtlassianAccountOnboardingLinks.AtlassianAccount,
            }),
          }}
        />
        <div className={styles.section}>
          <PrimaryButton onClick={atlassianLogin}>
            {format('log in with Atlassian button')}
          </PrimaryButton>
        </div>
      </OnboardingDialog>
    </>
  );
};
